import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../enums/eventsData";
import { LocalStorageKeys } from "../../enums/localStorageKeys";
import { RootState } from "../../redux/store/store";
import { getItemFromLocalStorage } from "../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import {
  editableTextCellEventNameMappingForChangeAction,
  editableTextCellEventNameMappingForClickAction,
} from "./helper/editableTextCellEventNameMapping";

interface EditableTextCellProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rowIndex?: number;
  fieldKey?: string;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: any,
  ) => void;
  hasError?: boolean;
}

const EditableTextCell: React.FC<EditableTextCellProps> = ({
  value,
  onValueChange,
  placeholder = "Enter text...",
  className = "",
  rowIndex,
  fieldKey,
  onTableRowUpdate,
  hasError = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();
  const logEventTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedLogEvent = useCallback((eventData: any) => {
    if (logEventTimeoutRef.current) {
      clearTimeout(logEventTimeoutRef.current);
    }
    logEventTimeoutRef.current = setTimeout(() => {
      apiWorkerManager.enqueueAndProcess(eventData);
      logEventTimeoutRef.current = null;
    }, 500);
  }, []);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    if (fieldKey) {
      const jCode = getItemFromLocalStorage(
        `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
      );
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name:
          editableTextCellEventNameMappingForClickAction[fieldKey] +
          " (" +
          jCode?.[rowIndex ?? 0] +
          ")",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          j_code: jCode?.[rowIndex ?? 0],
          payer_name: payerName,
        },
      });
    }
    setIsEditing(true);
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    if (fieldKey) {
      const jCode = getItemFromLocalStorage(
        `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
      );
      debouncedLogEvent({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: getOrgIdForFetchExternalWorklist() || "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        event_name:
          editableTextCellEventNameMappingForChangeAction[fieldKey] +
          " (" +
          jCode?.[rowIndex ?? 0] +
          ")",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
          j_code: jCode?.[rowIndex ?? 0],
          new_value: newValue,
          old_value: value,
          payer_name: payerName,
        },
      });
    }

    if (newValue !== value) {
      try {
        setIsUpdating(true);
        onValueChange(newValue);

        if (onTableRowUpdate && fieldKey && typeof rowIndex === "number") {
          onTableRowUpdate(rowIndex, fieldKey, newValue);
        }
      } catch (error) {
        console.error(`EditableTextCell: Error updating data:`, error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  return (
    <div
      className={`flex w-full cursor-pointer items-center rounded-md border p-1 transition-colors hover:bg-gray-50 ${className} ${isUpdating ? "opacity-50" : ""} ${hasError ? "border-red-500" : "border-primaryGray-15"}`}
      onClick={handleClick}
    >
      {isEditing ? (
        <div className="flex w-full items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={handleInputChange}
            placeholder={placeholder}
            className="w-full bg-transparent text-xs text-primaryGray-1 outline-none"
            disabled={isUpdating}
          />
          {isUpdating && (
            <div className="text-xs text-blue-500">Updating...</div>
          )}
        </div>
      ) : (
        <span className="w-full truncate text-xs text-primaryGray-1">
          {value?.toString() || placeholder}
        </span>
      )}
    </div>
  );
};

export default EditableTextCell;
